import { Fragment } from "react"
import { useTranslation } from "react-i18next"

import type { Span } from "types/spans.types"

import useSpanDevices from "helpers/hooks/devices/useSpanDevices"
import EntityLink from "widgets/common/EntityLink"
import NoticePanel from "widgets/common/NoticePanel"

/**
 * The span metrics still only queryable per device, by the `nameKey` of their
 * chart, so the sentence names them the way the charts are titled. Drop each one
 * as #6719 tags it with `span_id`.
 */
const DEVICE_ONLY_METRICS = ["SAG", "CONDUCTOR_TENSION", "BENDING_AMPLITUDE"]

interface SpanAvailabilityNoticeProps {
  span: Span
}

/**
 * Why some span charts are not here: they are still shown on the span's device.
 * A span with no device says so in its context block, so this has nothing to add.
 * It goes away metric by metric as #6719 tags `span_id`.
 */
const SpanAvailabilityNotice = ({ span }: SpanAvailabilityNoticeProps) => {
  const { t, i18n } = useTranslation()
  const { devices, isError, isLoading } = useSpanDevices({ spanId: span.id })

  // It would be a guess until the devices are in, or if they never arrive.
  if (isLoading || isError || devices.length === 0) {
    return null
  }

  // Chart titles are capitalised and these sit inside a sentence, so they go
  // down and only the sentence's first letter comes back up.
  const joined = new Intl.ListFormat(i18n.resolvedLanguage ?? "en", {
    type: "conjunction",
  }).format([
    ...DEVICE_ONLY_METRICS.map((metric) => t(`plot_titles.${metric}`).toLowerCase()),
    // The named ones are what an operator scans for; the list is longer.
    t("spans.OTHER_TELEMETRY"),
  ])
  const metrics = joined.charAt(0).toUpperCase() + joined.slice(1)

  const deviceLinks = devices.map((device, index) => (
    <Fragment key={device.less_id}>
      {index > 0 && ", "}
      <EntityLink entity="device" id={device.less_id} name={device.name} />
    </Fragment>
  ))

  return (
    <NoticePanel
      body={
        <>
          {t("spans.DISPLAYED_ON", { metrics })} {deviceLinks}.
        </>
      }
    />
  )
}

export default SpanAvailabilityNotice
